import { SUPPRESSION_REASON_LABELS, type SuppressionDTO } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, Button, EmptyState, Field, Input, Select, SidePanel, Skeleton, Textarea, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { api, apiUrl, errorMessage } from '../../lib/api';
import { formatDate } from '../../lib/format';

/**
 * Addresses that must never be emailed (unsubscribes, bounces, complaints, do-not-contact). Every send path checks
 * this list. Lifting an entry is a deliberate, reasoned, audited action — nothing is ever deleted.
 */
export function SuppressionsTab() {
  const { can } = useAuth();
  const [status, setStatus] = useState<'active' | 'lifted'>('active');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [lifting, setLifting] = useState<SuppressionDTO | null>(null);
  const query = { status, q: q || undefined, pageSize: 100 };
  const { data, isLoading } = useQuery({
    queryKey: ['suppressions', query],
    queryFn: ({ signal }) => api.get<{ items: SuppressionDTO[]; total: number; activeCount: number }>(apiUrl('/api/suppressions', query), { signal }),
  });
  const manage = can('suppression:manage');

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input className="max-w-xs" placeholder="Search address" value={q} onChange={(event) => setQ(event.target.value)} />
        <Select value={status} onChange={(event) => setStatus(event.target.value as 'active' | 'lifted')}>
          <option value="active">Active ({data?.activeCount ?? '…'})</option>
          <option value="lifted">Lifted</option>
        </Select>
        {manage && (
          <Button className="ml-auto" icon={Plus} onClick={() => setAdding(true)}>
            Add address
          </Button>
        )}
      </div>
      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : (data?.items.length ?? 0) === 0 ? (
        <EmptyState icon={ShieldOff} title="No suppressed addresses" description="Unsubscribes, hard bounces, complaints and do-not-contact leads land here automatically." />
      ) : (
        <ul className="divide-y divide-slate-100">
          {data!.items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-2 py-2 text-[13px]">
              <span className="font-medium text-slate-900">{item.email}</span>
              <Badge color={item.reason === 'unsubscribed' || item.reason === 'complaint' ? 'red' : 'amber'} size="sm">
                {SUPPRESSION_REASON_LABELS[item.reason]}
              </Badge>
              {item.businessId && (
                <Link to={`/leads/${item.businessId}`} className="text-slate-500 hover:text-ember-700">
                  {item.businessName}
                </Link>
              )}
              <span className="text-xs text-slate-400">since {formatDate(item.createdAt)}</span>
              {item.liftedAt && <span className="text-xs text-slate-500">lifted {formatDate(item.liftedAt)}: {item.liftReason}</span>}
              {manage && !item.liftedAt && (
                <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setLifting(item)}>
                  Lift
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {adding && <AddSuppression onClose={() => setAdding(false)} />}
      {lifting && <LiftSuppression suppression={lifting} onClose={() => setLifting(null)} />}
    </div>
  );
}

function AddSuppression({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const add = useMutation({
    mutationFn: () => api.post('/api/suppressions', { email, reason: 'manual', notes: notes || undefined }),
    onSuccess: async () => {
      toast.success('Address suppressed — queued emails to it were cancelled');
      await queryClient.invalidateQueries({ queryKey: ['suppressions'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  return (
    <SidePanel
      open
      onClose={onClose}
      title="Suppress an address"
      width="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!email} loading={add.isPending} onClick={() => add.mutate()}>
            Suppress
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label="Email address">
          <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
        <Field label="Why?">
          <Textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Asked by phone not to be emailed" />
        </Field>
      </div>
    </SidePanel>
  );
}

function LiftSuppression({ suppression, onClose }: { suppression: SuppressionDTO; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const lift = useMutation({
    mutationFn: () => api.post(`/api/suppressions/${suppression.id}/lift`, { reason }),
    onSuccess: async () => {
      toast.success('Suppression lifted');
      await queryClient.invalidateQueries({ queryKey: ['suppressions'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  return (
    <SidePanel
      open
      onClose={onClose}
      title={`Allow emails to ${suppression.email} again?`}
      description={
        suppression.reason === 'unsubscribed' || suppression.reason === 'complaint'
          ? 'This person unsubscribed or complained. Only lift this if they explicitly asked to hear from you again.'
          : 'Record why this address is safe to email again.'
      }
      width="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={reason.trim().length < 3} loading={lift.isPending} onClick={() => lift.mutate()}>
            Lift suppression
          </Button>
        </div>
      }
    >
      <Field label="Reason (kept in the audit log)">
        <Textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
      </Field>
    </SidePanel>
  );
}
